
import React from "react";
import NumericInput from "@/components/ui/NumericInput";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { AlertCircle, CheckCircle, Shield } from "lucide-react";
import type { MortgageData } from "../../CalculatorTabs";
import { formatLargeAmount, parseCurrency, formatPercentage, parsePercentage } from "../../utils/formatters";
import { AutoCalculateOptions } from "../../utils/types";
import { MetricTooltip } from "../../advanced/analytics/MetricTooltip";

interface DownPaymentInputProps {
  mortgageData: MortgageData;
  setMortgageData: React.Dispatch<React.SetStateAction<MortgageData>>;
  autoCalculate: AutoCalculateOptions;
  onDownPaymentChange: (value: number) => void;
}

const DownPaymentInput: React.FC<DownPaymentInputProps> = ({ 
  mortgageData, 
  setMortgageData, 
  autoCalculate, 
  onDownPaymentChange 
}) => {
  // Get minimum down payment based on loan type
  const getMinDownPayment = () => {
    switch (mortgageData.loanType) {
      case 'fha':
        return 3.5;
      case 'va':
      case 'usda':
        return 0;
      case 'conventional':
      default:
        return mortgageData.isFirstTimeHomebuyer ? 3 : 5;
    }
  };

  // Down payment is always editable; zero-down loans default to 0% but can be overridden
  const isDownPaymentDisabled = () => {
    return false;
  };

  const minDownPaymentPercentage = getMinDownPayment();
  const isVALoan = mortgageData.loanType === 'va';
  const isUSDALoan = mortgageData.loanType === 'usda';
  const isZeroDownLoan = isVALoan || isUSDALoan;
  const hasVADisability = mortgageData.vaDisabilityStatus === 'partial' || mortgageData.vaDisabilityStatus === 'full';
  const isDisabled = isDownPaymentDisabled();
  
  // Calculate PMI threshold (20% for conventional, N/A for others)
  const pmiThreshold = mortgageData.loanType === 'conventional' ? 20 : 0;
  const willRequirePMI = mortgageData.loanType === 'conventional' && mortgageData.downPaymentPercentage < 20;

  const handleDownPaymentAmountChange = (value: number) => {
    if (!isDisabled) {
      onDownPaymentChange(value);
    }
  };

  const handleDownPaymentPercentageChange = (value: number) => {
    if (isDisabled) {
      return;
    }
    
    // Calculate dollar amount from percentage
    const newDownPaymentAmount = Math.round((value / 100) * mortgageData.homePrice);
    onDownPaymentChange(newDownPaymentAmount);
  };

  const getValidationStatus = () => {
    if (isZeroDownLoan) {
      return { isValid: true, message: 'No down payment required', type: 'success' as const };
    }
    
    if (hasVADisability) {
      return { 
        isValid: true, 
        message: mortgageData.vaDisabilityStatus === 'full' 
          ? 'No down payment required (100% VA disability)' 
          : 'No down payment required (VA disability)', 
        type: 'success' as const 
      };
    }
    
    if (mortgageData.downPaymentPercentage < minDownPaymentPercentage) {
      return { 
        isValid: false, 
        message: `Minimum ${minDownPaymentPercentage}% required for ${mortgageData.loanType.toUpperCase()} loans`, 
        type: 'error' as const 
      };
    }
    
    if (willRequirePMI) {
      return { 
        isValid: true, 
        message: 'PMI required (less than 20% down)', 
        type: 'warning' as const 
      };
    }
    
    return { isValid: true, message: 'Down payment meets requirements', type: 'success' as const };
  };

  const validationStatus = getValidationStatus();
  
  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <Label className="text-sm font-medium">Down Payment <MetricTooltip metric="downPayment" /></Label>
        <div className="flex items-center gap-2">
          {validationStatus.type === 'success' && (
            <CheckCircle className="w-4 h-4 text-green-600" />
          )}
          {validationStatus.type === 'warning' && (
            <AlertCircle className="w-4 h-4 text-orange-600" />
          )}
          {validationStatus.type === 'error' && (
            <AlertCircle className="w-4 h-4 text-red-600" />
          )}
          {mortgageData.vaDisabilityStatus === 'full' && (
            <Shield className="w-4 h-4 text-blue-600" />
          )}
          <Badge 
            variant={validationStatus.type === 'error' ? 'destructive' : 'outline'}
            className="text-xs"
          >
            {mortgageData.vaDisabilityStatus === 'full' ? '100% VA Disabled' : `Min: ${minDownPaymentPercentage}%`}
          </Badge>
        </div>
      </div>

      {/* VA 100% Disability Notice */}
      {mortgageData.vaDisabilityStatus === 'full' && (
        <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg">
          <div className="flex items-center gap-2 text-blue-700 text-sm">
            <Shield className="w-4 h-4" />
            <div>
              <div className="font-medium">100% VA Disability Benefit</div>
              <div className="text-xs mt-1">
                As a 100% disabled veteran, you qualify for $0 down payment and no funding fee.
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Down Payment Amount */}
      <NumericInput
        label="Down Payment ($)"
        value={mortgageData.downPayment}
        onChange={handleDownPaymentAmountChange}
        formatValue={formatLargeAmount}
        parseValue={parseCurrency}
        min={0}
        max={mortgageData.homePrice}
        step={1000}
        showSlider={true}
        sliderStep={1000}
        labelClassName="text-sm font-medium"
        inputClassName="w-24 text-right"
        disabled={isDisabled}
      />

      {/* Down Payment Percentage */}
      <NumericInput
        label="Down Payment (%)"
        value={mortgageData.downPaymentPercentage}
        onChange={handleDownPaymentPercentageChange}
        formatValue={formatPercentage}
        parseValue={parsePercentage}
        min={minDownPaymentPercentage}
        max={100}
        step={0.5}
        showSlider={true}
        sliderStep={0.5}
        labelClassName="text-sm font-medium"
        inputClassName="w-24 text-right"
        disabled={isDisabled}
      />
      
      {/* Status Message */}
      <div className={`text-xs p-2 rounded-lg border ${
        validationStatus.type === 'success' ? 'bg-green-50 border-green-200 text-green-700' :
        validationStatus.type === 'warning' ? 'bg-orange-50 border-orange-200 text-orange-700' :
        'bg-red-50 border-red-200 text-red-700'
      }`}>
        {validationStatus.message}
      </div>

      {/* Loan Amount Display */}
      {mortgageData.homePrice > 0 && (
        <div className="p-3 bg-muted/30 rounded-lg border">
          <div className="grid grid-cols-2 gap-4 text-sm">
            <div>
              <span className="text-muted-foreground">Loan Amount:</span>
              <div className="font-semibold">
                {formatLargeAmount(mortgageData.homePrice - mortgageData.downPayment)}
              </div>
            </div>
            <div>
              <span className="text-muted-foreground">Down Payment:</span>
              <div className="font-semibold">
                {formatLargeAmount(mortgageData.downPayment)} ({mortgageData.downPaymentPercentage.toFixed(1)}%)
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DownPaymentInput;
